<script lang="ts">
import { defineComponent, PropType } from 'vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import ButtonPrev from '@/components/button/ButtonPrev.vue'
import SelectorScale from '@/components/lead/SelectorScale.vue'
import SelectorListingStatus from '@/components/lead/SelectorListingStatus.vue'
import InputCustomFieldItems from '@/components/customFiled/InputCustomFieldItems.vue'
import InputHearingFieldItems from '@/components/hearingField/InputHearingFieldItems.vue'
import InputFormPhoneNumber from '@/components/lead/InputFormPhoneNumber.vue'
import PaginationRegistrationStep from '@/components/user/registration/PaginationRegistrationStep.vue'
import ValidationObserver from '@/components/validation/ValidationObserver.vue'
import validationOperator from '@/models/validationOperator'
import hearingFieldsUtil from '@/models/hearingFieldsUtil'

export default defineComponent({
  name: 'InputStep3UserRegistrationForShowContents',

  components: {
    ButtonSubmit,
    ButtonPrev,
    SelectorScale,
    SelectorListingStatus,
    InputCustomFieldItems,
    InputHearingFieldItems,
    InputFormPhoneNumber,
    PaginationRegistrationStep,
    ValidationObserver,
  },

  props: {
    namespace: {
      type: String,
      default: '',
    },

    scale: {
      type: Object as PropType<InputText>,
      required: true,
    },

    listingStatus: {
      type: Object as PropType<InputText>,
      required: true,
    },

    phoneNumber: {
      type: Object as PropType<InputText>,
      required: true,
    },

    customFieldConfigItems: {
      type: Array as PropType<CustomFieldConfig[]>,
      required: true,
    },

    inputCustomFields: {
      type: Object as PropType<Inputs>,
      required: true,
    },

    hearingFieldConfig: {
      type: Object as PropType<HearingFieldConfig>,
      required: true,
    },

    inputHearingFields: {
      type: Object as PropType<Inputs>,
      required: true,
    },

    additionalInputs: {
      type: Object as PropType<Inputs>,
      default: () => ({}),
    },
  },

  emits: [
    'update:scale',
    'update:listingStatus',
    'update:phoneNumber',
    'update:customFields',
    'update:hearingFields',
    'select:hearingMultiSelectPrimary',
    'close:hearingMultiSelectSecondary',
    'click:next',
    'click:prev',
    'error',
  ],

  setup(props, { emit }) {
    const { validationObserver, validate } = validationOperator()
    const { hasHearingFields } = hearingFieldsUtil(props.hearingFieldConfig, props.inputHearingFields)

    const next = () => {
      emit('click:next')
    }

    /**
     * ヒアリング項目の「その他」に該当する項目が選択された際の処理
     *
     * @param name 対応する追加設問の name 属性値
     * @param value 対応する追加設問の入力要素オブジェクト
     * @description 引数は emit で通知される値を暗黙的に受け取る
     */
    const onSelectInHearingMultiSelectorPrimary = (name: string, value: Input) => {
      emit('select:hearingMultiSelectPrimary', name, value)
    }

    return {
      // data
      validationObserver,

      // computed
      hasHearingFields,

      // methods
      validate,
      next,
      onSelectInHearingMultiSelectorPrimary,
    }
  }
})
</script>

<template>
  <ValidationObserver
    ref="validationObserver"
    :namespace="namespace"
    @valid="next">
    <div class="o-heading__section">
      <h2 class="o-heading__section--item">
        <slot name="heading">
          資料を読むには次の項目をご入力ください
        </slot>
      </h2>
    <!-- /.o-heading__section --></div>

    <PaginationRegistrationStep :current-step="3" />

    <div class="l-rows">
      <!-- 従業員規模 -->
      <div
        v-if="scale"
        class="l-rows--row">
        <SelectorScale
          :scale="scale"
          :namespace="namespace"
          @update:scale="$emit('update:scale', $event)" />
      <!-- /.l-rows row --></div>

      <!-- 上場区分 -->
      <div
        v-if="listingStatus && listingStatus.name"
        class="l-rows--row">
        <SelectorListingStatus
          :listing-status="listingStatus"
          :namespace="namespace"
          @update:listing-status="$emit('update:listingStatus', $event)" />
      <!-- /.l-rows row --></div>

      <!-- 勤務先電話番号 -->
      <div
        v-if="phoneNumber"
        class="l-rows--row">
        <InputFormPhoneNumber
          :model-value="phoneNumber.value"
          :namespace="namespace"
          :name="phoneNumber.name"
          :validation-scheme="phoneNumber.validationScheme"
          @update:model-value="$emit('update:phoneNumber', $event)" />
      <!-- /.l-rows row --></div>

      <!-- 郵便番号 -->
      <!-- <div
        v-if="corporateZipcode"
        class="l-rows--row">
        <InputFormPostalCode
          :model-value="corporateZipcode.value"
          :namespace="namespace"
          :name="corporateZipcode.name"
          :validation-scheme="corporateZipcode.validationScheme"
          @search:address="$emit('update:corporateAddress1', $event)"
          @update:model-value="$emit('update:corporateZipcode', $event)" />
      </div> -->

      <!-- 住所 -->
      <!-- <div
        v-if="corporateAddress1"
        class="l-rows--row">
        <InputForm
          :model-value="corporateAddress1.value"
          :namespace="namespace"
          :name="corporateAddress1.name"
          header="住所"
          :validation-scheme="corporateAddress1.validationScheme"
          @update:model-value="$emit('update:corporateAddress1', $event)" />
      </div> -->

      <!-- 建物名 -->
      <!-- <div
        v-if="corporateAddress2"
        class="l-rows--row">
        <InputForm
          :model-value="corporateAddress2.value"
          :namespace="namespace"
          :name="corporateAddress2.name"
          header="建物名"
          :validation-scheme="corporateAddress2.validationScheme"
          @update:model-value="$emit('update:corporateAddress2', $event)" />
      </div> -->
    <!-- /.l-rows --></div>

    <div class="o-note">
      <p class="o-note--item u-text__small">
        正確な情報のご入力をお願いいたします。虚偽や不正があった場合は、サービスの利用を停止させていただく場合があります。
      </p>
    <!-- /.o-note --></div>

    <div class="div">
      <slot
        name="additionalInputs"
        :namespace="namespace" />
    </div>

    <!-- アンケートエリア -->
    <InputCustomFieldItems
      :namespace="namespace"
      :custom-field-config-items="customFieldConfigItems"
      :input-custom-fields="inputCustomFields"
      @update:custom-fields="$emit('update:customFields', $event)" />

    <!-- ヒアリング項目 -->
    <InputHearingFieldItems
      v-if="hasHearingFields"
      :namespace="namespace"
      :hearing-field-config="hearingFieldConfig"
      :input-hearing-fields="inputHearingFields"
      @update:hearing-fields="$emit('update:hearingFields', $event)"
      @select:primary="onSelectInHearingMultiSelectorPrimary"
      @close:secondary="$emit('close:hearingMultiSelectSecondary', $event)" />

    <ButtonSubmit
      :is-link="false"
      :has-shadow="false"
      prevent
      stop
      @click:submit="validate">
      次へ
    </ButtonSubmit>

    <ButtonPrev
      :is-link="false"
      @click:prev="$emit('click:prev')" />
  </ValidationObserver>
</template>

<style lang="sass" scoped>
// object
.o-heading__section
  & &--item
    font:
      size: $font-normal
      weight: bold
    text-align: center

  + .p
    margin-top: 0.5em
</style>
